import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import DiceContainer from '../dice/DiceContainer';
import GameControls from './GameControls';
import GameModal from './GameModal';
import RewardDiceModal from './RewardDiceModal';
import {
  GamePhase,
  GameState,
  GameSettings as GameSettingsType,
  Die,
  PlayerDiceConfig,
} from '../../types';
import { initializeDice } from '../../core/dice';
import { hasValidSelection, calculateSelectionScore, hasPossibleScoring } from '../../core/scoring';
import { ComputerPlayer } from '../../core/ai/computerPlayer';
import '../../styles/Game.css';
import LandingPage from '../landing/LandingPage';
import DiceSelector from '../dice/DiceSelector';
import DiceShop from '../dice/DiceShop';
import LockedDiceContainer from '../dice/LockedDiceContainer';
import { getUserData, saveUserData, updateUserGroschen } from '../../services/LocalStorageService';
import { useGamePersistence } from '../../hooks/useGamePersistence';
import { useChallengerMode } from '../../hooks/useChallengerMode';
import { useDiceShop } from '../../hooks/useDiceShop';
import { useBetting } from '../../hooks/useBetting';
import { GroschenService } from '../../services/GroschenService';
import {
  DiceType,
  getDiceName,
  rollDice as rollDiceByType,
  getRandomRewardDice,
  getDiceMaxOwned
} from '../../models/DiceModel';
import { getRandomChallengerRewardDice } from '../../models/ChallengerModel';
import { useLanguage } from '../../i18n';

const initialSettings: GameSettingsType = {
  targetScore: 4000,
  computerDifficulty: 'medium'
};

const initialState: GameState = {
  dice: initializeDice(),
  players: [
    {
      name: 'player_0',
      score: 0,
      turnScore: 0,
      isComputer: false,
      groschen: 50,
      ownedDice: Array(6).fill(DiceType.NORMAL)
    },
    {
      name: 'computer_1',
      score: 0,
      turnScore: 0,
      isComputer: true,
      groschen: 0,
      ownedDice: [DiceType.NORMAL]
    }
  ],
  currentPlayerIndex: 0,
  phase: GamePhase.BETTING,
  rollCount: 0,
  settings: initialSettings,
  winner: null,
  bet: 0,
  diceConfigs: {
    diceConfigs: Array(6).fill(DiceType.NORMAL)
  },
  isFarkle: false
};


const Game: React.FC = () => {
  const { t } = useLanguage();

  const [gameState, setGameState] = useState<GameState>(() => {
    const userData = getUserData();

    if (userData) {
      return {
        ...initialState,
        players: [
          {
            ...initialState.players[0],
            groschen: userData.groschen,
            ownedDice: userData.ownedDice
          },
          initialState.players[1]
        ],
        diceConfigs: userData.diceConfigs,
        settings: userData.gameSettings || initialSettings
      };
    }

    saveUserData(
      initialState.players[0],
      initialState.diceConfigs,
      initialSettings
    );
    return initialState;
  });
  const [showDiceSelector, setShowDiceSelector] = useState(false);
  const [showDiceShop, setShowDiceShop] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showGameScreen, setShowGameScreen] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', content: '' });
  const [selectionScore, setSelectionScore] = useState(0);
  const [isForfeitConfirmation, setIsForfeitConfirmation] = useState(false);
  const [rewardDice, setRewardDice] = useState<DiceType | null>(null);

  const rollDie = (type: DiceType): number => rollDiceByType(type);

  const { challengerMode, resetChallengerMode, handleStartChallengerGame } = useChallengerMode({
    gameState,
    setGameState,
    setShowGameScreen,
    setShowModal,
    setModalContent,
    initializeDiceWithConfig: () => initializeDiceWithConfig(),
    initialState,
  });

  const initializeDiceWithConfig = () => {
    if (challengerMode.isActive && gameState.currentPlayerIndex === 1) {
      return Array(6).fill(null).map((_, index) => ({
        id: Math.random(),
        value: rollDie(challengerMode.challengerDiceConfig[index]),
        selected: false,
        locked: false,
        type: challengerMode.challengerDiceConfig[index]
      }));
    }
    return Array(6).fill(null).map((_, index) => ({
      id: Math.random(),
      value: rollDie(gameState.currentPlayerIndex === 0 ? gameState.diceConfigs.diceConfigs[index] : DiceType.NORMAL),
      selected: false,
      locked: false,
      type: gameState.currentPlayerIndex === 0 ? gameState.diceConfigs.diceConfigs[index] : DiceType.NORMAL
    }));
  };

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isComputerTurn = currentPlayer.isComputer;
  const hasSelectedDice = gameState.dice.some(die => die.selected);

  // Helper to get display name for a player
  const getPlayerDisplayName = (player: { name: string; isComputer: boolean }) => {
    if (!player.isComputer) return t('player');
    // For challengers, name is already translated from ChallengerModel
    if (player.name === 'computer_1') return t('computer');
    return player.name;
  };

  const handleDieClick = (id: number) => {
    if (gameState.phase !== GamePhase.SELECT || isComputerTurn) return;

    setGameState(prevState => {
      const clickedDie = prevState.dice.find(die => die.id === id);

      if (!clickedDie || clickedDie.locked) {
        return prevState;
      }

      const newDice = prevState.dice.map(die => {
        if (die.id === id) {
          return { ...die, selected: !die.selected };
        }
        return die;
      });

      const selectedDice = newDice.filter(die => die.selected && !die.locked);
      const message = selectedDice.length > 0 ?
        hasValidSelection(newDice) ? t('selectToKeep') : t('invalidSelection')
        : t('pleaseSelect');

      return {
        ...prevState,
        dice: newDice,
        message
      };
    });
  };

  useEffect(() => {
    const score = calculateSelectionScore(gameState.dice);
    setSelectionScore(score);
  }, [gameState.dice]);

  const handleStartGame = () => {
    setShowGameScreen(true);
    setGameState(prevState => {
      const newState = {
        ...initialState,
        phase: GamePhase.ROLL,
        settings: prevState.settings,
        diceConfigs: prevState.diceConfigs,
        dice: initializeDiceWithConfig(),
        players: [
          {
            name: 'player_0',
            score: 0,
            turnScore: 0,
            isComputer: false,
            groschen: prevState.players[0].groschen,
            ownedDice: prevState.players[0].ownedDice
          },
          {
            name: 'computer_1',
            score: 0,
            turnScore: 0,
            isComputer: true,
            groschen: 0,
            ownedDice: [DiceType.NORMAL]
          }
        ],
        bet: prevState.bet
      };

      GroschenService.setGroschen(
        newState.players[0],
        newState.diceConfigs,
        newState.players[0].groschen,
        'start_new_game',
        newState.settings
      );

      return newState;
    });
  };

  const handleRoll = () => {
    if (gameState.phase === GamePhase.ROLL) {
      setGameState(prevState => {
        const newDice = initializeDiceWithConfig();

        if (!hasPossibleScoring(newDice)) {
          return {
            ...prevState,
            dice: newDice,
            phase: GamePhase.END_TURN,
            isFarkle: true,
            players: prevState.players.map((player, idx) => ({
              ...player,
              turnScore: idx === prevState.currentPlayerIndex ? 0 : player.turnScore
            }))
          };
        }

        return {
          ...prevState,
          dice: newDice,
          phase: GamePhase.SELECT,
          rollCount: prevState.rollCount + 1
        };
      });
    } else if (gameState.phase === GamePhase.SELECT) {
      setGameState(prevState => {
        const currentSelectionScore = calculateSelectionScore(prevState.dice);

        if (currentSelectionScore <= 0) {
          setGameState(state => ({
            ...state,
            message: t('invalidSelectRetry')
          }));
          return prevState;
        }

        const unlockedCount = prevState.dice.filter(die => !die.locked).length;

        const updatedDice = prevState.dice.map(die => {
          if (die.selected) {
            return { ...die, selected: false, locked: true };
          }
          return die;
        });

        const newPlayers = prevState.players.map((player, idx) => {
          if (idx === prevState.currentPlayerIndex) {
            return {
              ...player,
              turnScore: player.turnScore + currentSelectionScore
            };
          }
          return player;
        });

        if (unlockedCount === 0 || updatedDice.every(die => die.locked)) {
          const newInitializedDice = initializeDiceWithConfig();

          const hasPossible = hasPossibleScoring(newInitializedDice);

          if (!hasPossible) {
            return {
              ...prevState,
              dice: newInitializedDice,
              phase: GamePhase.END_TURN,
              isFarkle: true,
              players: prevState.players.map((player, idx) => ({
                ...player,
                turnScore: idx === prevState.currentPlayerIndex ? 0 : player.turnScore
              }))
            };
          }

          return {
            ...prevState,
            dice: newInitializedDice,
            players: newPlayers,
            phase: GamePhase.SELECT
          };
        }

        const newDice = updatedDice.filter(die => !die.locked).map(die => ({
          ...die,
          value: rollDie(die.type),
          selected: false
        }));

        const lockedDice = updatedDice.filter(die => die.locked);
        const allDice = [...lockedDice, ...newDice];

        if (!hasPossibleScoring(allDice)) {
          return {
            ...prevState,
            dice: allDice,
            phase: GamePhase.END_TURN,
            isFarkle: true,
            players: newPlayers.map((player, idx) => ({
              ...player,
              turnScore: idx === prevState.currentPlayerIndex ? 0 : player.turnScore
            }))
          };
        }

        return {
          ...prevState,
          dice: allDice,
          players: newPlayers,
          phase: GamePhase.SELECT,
          rollCount: prevState.rollCount + 1
        };
      });
    } else {
      setGameState(prevState => {
        const nextPlayerIndex = (prevState.currentPlayerIndex + 1) % prevState.players.length;

        if (prevState.isFarkle) {
          return {
            ...prevState,
            currentPlayerIndex: nextPlayerIndex,
            phase: GamePhase.ROLL,
            isFarkle: false,
            rollCount: 0
          };
        } else {
          return {
            ...prevState,
            currentPlayerIndex: nextPlayerIndex,
            phase: GamePhase.ROLL,
            dice: prevState.dice.map((die: any) => ({ ...die, selected: false })),
            rollCount: 0
          };
        }
      });
    }
  };

  const calculateAndUpdateScore = (dice: Die[], playerIndex: number, endTurn: boolean = false) => {
    return (prevState: GameState) => {
      const selectedDice = dice.filter(die => die.selected && !die.locked);
      const selectionScore = selectedDice.length > 0 ? calculateSelectionScore(selectedDice) : 0;

      const newPlayers = [...prevState.players];

      if (endTurn) {
        const totalTurnScore = newPlayers[playerIndex].turnScore + selectionScore;
        const totalScore = newPlayers[playerIndex].score + totalTurnScore;

        newPlayers[playerIndex] = {
          ...newPlayers[playerIndex],
          score: totalScore,
          turnScore: 0
        };

        const updatedDice = prevState.dice.map(die => {
          if (die.selected) {
            return { ...die, selected: false, locked: true };
          }
          return die;
        });

        const isGameOver = totalScore >= (challengerMode.isActive ? challengerMode.challengerTargetScore : prevState.settings.targetScore);

        if (isGameOver) {
          const winner = newPlayers[playerIndex];

          if (!challengerMode.isActive && playerIndex === 0) {
            const betReward = prevState.bet * 2;
            newPlayers[0] = {
              ...newPlayers[0],
              groschen: newPlayers[0].groschen + betReward
            };

            updateUserGroschen(newPlayers[0].groschen);
          }

          setTimeout(() => handleGameOver(playerIndex), 100);

          if (playerIndex === 0 && !challengerMode.isActive) {
            const rewardDiceType = getRandomRewardDice();

            if (rewardDiceType) {
              const ownedCount = newPlayers[0].ownedDice.filter(type => type === rewardDiceType).length;
              const maxOwned = getDiceMaxOwned(rewardDiceType);

              if (ownedCount < maxOwned) {
                setRewardDice(rewardDiceType);
              }
            }
          }

          return {
            ...prevState,
            players: newPlayers,
            dice: updatedDice,
            phase: GamePhase.GAME_OVER,
            winner: winner
          };
        }

        const nextPlayerIndex = (playerIndex + 1) % prevState.players.length;
        const playerName = getPlayerDisplayName(prevState.players[playerIndex]);

        return {
          ...prevState,
          players: newPlayers,
          dice: updatedDice,
          message: t('endTurnMsg', { name: playerName, turnScore: totalTurnScore, totalScore }),
          currentPlayerIndex: nextPlayerIndex,
          phase: GamePhase.ROLL,
          rollCount: 0
        };
      } else {
        newPlayers[playerIndex] = {
          ...newPlayers[playerIndex],
          turnScore: newPlayers[playerIndex].turnScore + selectionScore
        };

        return {
          ...prevState,
          players: newPlayers,
          dice: prevState.dice.map(die => {
            if (die.selected) {
              return { ...die, selected: false, locked: true };
            }
            return die;
          }),
          message: t('selectionScoreMsg', { selection: selectionScore, turn: newPlayers[playerIndex].turnScore })
        };
      }
    };
  };

  const handleBank = () => {
    if (gameState.phase !== GamePhase.SELECT) return;
    setGameState(calculateAndUpdateScore(gameState.dice, gameState.currentPlayerIndex, true));
  };

  const handleSaveSettings = (settings: GameSettingsType) => {
    setGameState(prevState => {
      const newState = {
        ...prevState,
        settings
      };
      saveUserData(newState.players[0], newState.diceConfigs, settings);
      return newState;
    });
  };

  const computerPlay = useCallback(() => {
    if (!currentPlayer.isComputer) return;

    const executeComputerAction = () => {
      if (gameState.phase === GamePhase.ROLL) {
        setGameState(prevState => ({
          ...prevState,
          message: t('computerRolling')
        }));
        setTimeout(() => handleRoll(), 1000);
      } else if (gameState.phase === GamePhase.SELECT) {
        try {
          const selectDice = (diceIndices: number[]) => {
            setGameState(prevState => ({
              ...prevState,
              dice: prevState.dice.map((die, idx) => ({
                ...die,
                selected: diceIndices.includes(idx) || (die.selected && !die.locked)
              })),
              message: t('computerThinking', { count: diceIndices.length })
            }));
          };

          ComputerPlayer.executeComputerTurn(
            gameState.dice,
            currentPlayer.turnScore,
            currentPlayer.score,
            challengerMode.isActive ? challengerMode.challengerTargetScore : gameState.settings.targetScore,
            gameState.settings.computerDifficulty,
            selectDice,
            handleRoll,
            () => {
              setTimeout(() => {
                setGameState(prevState => {
                  const updatedDice = prevState.dice;
                  return calculateAndUpdateScore(updatedDice, prevState.currentPlayerIndex, true)(prevState);
                });
              }, 100);
            }
          );
        } catch (error) {
          console.error("Computer AI error:", error);
          handleBank();
        }
      } else if (gameState.phase === GamePhase.END_TURN) {
        handleRoll();
      }
    };

    const timeoutId = setTimeout(executeComputerAction, 1000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, gameState.phase, gameState.settings.targetScore, gameState.settings.computerDifficulty]);

  useEffect(() => {
    if (isComputerTurn && gameState.phase !== GamePhase.GAME_OVER) {
      computerPlay();
    }
  }, [isComputerTurn, gameState.phase, computerPlay]);

  const { handlePlaceBet } = useBetting({
    setGameState,
    setShowGameScreen,
    initializeDiceWithConfig,
    initialState,
  });

  const handleForfeit = () => {
    setModalContent({
      title: t('forfeitTitle'),
      content: t('forfeitContent', { bet: gameState.bet })
    });
    setShowModal(true);
    setIsForfeitConfirmation(true);
  };

  const handleConfirmForfeit = () => {
    setShowModal(false);
    setIsForfeitConfirmation(false);
    setShowGameScreen(false);

    setGameState(prevState => {
      const newState = {
        ...initialState,
        phase: GamePhase.BETTING,
        diceConfigs: prevState.diceConfigs,
        players: [
          {
            ...initialState.players[0],
            groschen: prevState.players[0].groschen,
            ownedDice: prevState.players[0].ownedDice
          },
          { ...initialState.players[1] }
        ],
        settings: prevState.settings
      };

      GroschenService.setGroschen(
        newState.players[0],
        newState.diceConfigs,
        newState.players[0].groschen,
        'forfeit',
        prevState.settings
      );

      return newState;
    });

    resetChallengerMode();
  };

  const handleCloseModal = () => {
    setShowModal(false);

    if (isForfeitConfirmation) {
      setIsForfeitConfirmation(false);
      return;
    }

    if (gameState.phase === GamePhase.GAME_OVER) {
      if (rewardDice) {
        setShowRewardModal(true);
        return;
      }
      resetGameAndReturnToHome();
    }
  };

  const handleCloseRewardModal = () => {
    if (rewardDice) {
      setGameState(prevState => {
        const ownedCount = prevState.players[0].ownedDice.filter(type => type === rewardDice).length;
        const maxOwned = getDiceMaxOwned(rewardDice);

        if (ownedCount < maxOwned) {
          const newOwnedDice = [...prevState.players[0].ownedDice, rewardDice];

          const updatedPlayer = GroschenService.updateOwnedDice(
            prevState.players[0],
            prevState.diceConfigs,
            newOwnedDice,
            `reward_${getDiceName(rewardDice)}`
          );

          return {
            ...prevState,
            players: [
              updatedPlayer,
              ...prevState.players.slice(1)
            ]
          };
        }

        return prevState;
      });
    }

    setShowRewardModal(false);
    setRewardDice(null);
    resetGameAndReturnToHome();
  };

  const resetGameAndReturnToHome = () => {
    setGameState(prevState => {
      const newState = {
        ...initialState,
        phase: GamePhase.BETTING,
        diceConfigs: prevState.diceConfigs,
        players: [
          {
            ...initialState.players[0],
            groschen: prevState.players[0].groschen,
            ownedDice: prevState.players[0].ownedDice
          },
          { ...initialState.players[1] }
        ],
        settings: prevState.settings
      };

      GroschenService.setGroschen(
        newState.players[0],
        newState.diceConfigs,
        newState.players[0].groschen,
        'game_over_reset',
        prevState.settings
      );

      return newState;
    });

    resetChallengerMode();
    setShowGameScreen(false);
  };

  const handleSaveDiceConfig = (config: PlayerDiceConfig) => {
    setGameState(prevState => {
      const newState = {
        ...prevState,
        diceConfigs: config
      };

      GroschenService.setGroschen(
        newState.players[0],
        config,
        newState.players[0].groschen,
        'save_dice_config',
        prevState.settings
      );

      return newState;
    });
  };

  const { handleBuyDice, handleSellDice } = useDiceShop({
    gameState,
    setGameState,
    setShowModal,
    setModalContent,
  });

  useGamePersistence(gameState);

  const handleGameOver = (playerIndex: number) => {
    const player = gameState.players[playerIndex];
    const playerName = getPlayerDisplayName(player);
    let content = t('playerWins', { name: playerName });

    if (!challengerMode.isActive) {
      if (playerIndex === 0) {
        const betReward = gameState.bet * 2;
        content += t('wonGroschen', { amount: betReward });

        const updatedPlayer = GroschenService.addGroschen(
          player,
          gameState.diceConfigs,
          betReward,
          'game_win_reward'
        );

        setGameState(prevState => ({
          ...prevState,
          players: [
            {
              ...updatedPlayer,
              score: prevState.players[0].score
            },
            ...prevState.players.slice(1)
          ]
        }));

        content += t('currentGroschen', { amount: updatedPlayer.groschen });
      } else {
        content += t('lostGroschen', { amount: gameState.bet });
        content += t('currentGroschen', { amount: gameState.players[0].groschen });
      }
    } else if (challengerMode.isActive && challengerMode.challenger) {
      const challengerName = challengerMode.challenger.name;
      if (playerIndex === 0) {
        const betReward = challengerMode.betAmount * 2;
        content += t('defeatedChallenger', { name: challengerName, amount: betReward });

        const updatedPlayer = GroschenService.addGroschen(
          player,
          gameState.diceConfigs,
          betReward,
          `defeat_${challengerName}`
        );

        const rewardDiceType = getRandomChallengerRewardDice(challengerMode.challenger.id);

        if (rewardDiceType) {
          const maxOwnedDiceOfType = getDiceMaxOwned(rewardDiceType);
          const ownedDiceOfType = player.ownedDice.filter(d => d === rewardDiceType).length;

          if (ownedDiceOfType < maxOwnedDiceOfType) {
            setRewardDice(rewardDiceType);
            content += t('gotSpecialDice', { name: getDiceName(rewardDiceType) });
            content += t('currentGroschen', { amount: updatedPlayer.groschen });

            setGameState(prevState => ({
              ...prevState,
              players: [
                {
                  ...updatedPlayer,
                  score: prevState.players[0].score
                },
                ...prevState.players.slice(1)
              ]
            }));
          } else {
            setGameState(prevState => ({
              ...prevState,
              players: [
                {
                  ...updatedPlayer,
                  score: prevState.players[0].score
                },
                ...prevState.players.slice(1)
              ]
            }));

            content += t('congratsDefeat', { name: challengerName });
            content += t('currentGroschen', { amount: updatedPlayer.groschen });
          }
        } else {
          setGameState(prevState => ({
            ...prevState,
            players: [
              {
                ...updatedPlayer,
                score: prevState.players[0].score
              },
              ...prevState.players.slice(1)
            ]
          }));

          content += t('congratsDefeat', { name: challengerName });
          content += t('currentGroschen', { amount: updatedPlayer.groschen });
        }
      } else {
        content += t('challengerWins', { name: challengerName });
        content += t('challengerLostGroschen', { amount: challengerMode.betAmount });
        content += t('currentGroschen', { amount: gameState.players[0].groschen });
      }
    }

    setModalContent({
      title: t('gameOver'),
      content: content
    });
    setShowModal(true);
  };

  return (
    <>
      {!showGameScreen && (
        <LandingPage
          onStartGame={handleStartGame}
          onSaveSettings={handleSaveSettings}
          onPlaceBet={handlePlaceBet}
          initialSettings={gameState.settings}
          playerGroschen={gameState.players[0].groschen}
          onOpenDiceSelector={() => setShowDiceSelector(true)}
          onOpenDiceShop={() => setShowDiceShop(true)}
          onStartChallengerGame={handleStartChallengerGame}
        />
      )}

      {showGameScreen && (
        <Container className="game-container">
          <Row className="header">
            <Col>
              <h1>{t('gameTitle')}</h1>
              <div className="header-buttons">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="forfeit-button"
                  onClick={handleForfeit}
                >
                  {t('forfeit')}
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="main-content">
            <Col xs={12}>
              <div className="total-scores-panel">
                <div className="total-scores-row">
                  <div className={`total-score-item ${gameState.currentPlayerIndex === 0 ? 'current-turn' : ''}`}>
                    <div className="total-score-label">{t('playerTotalScore')}</div>
                    <div className="total-score-value">{gameState.players[0].score}</div>
                  </div>
                  <div className={`total-score-item ${gameState.currentPlayerIndex === 1 ? 'current-turn' : ''}`}>
                    <div className="total-score-label">{t('opponentTotalScore', { name: getPlayerDisplayName(gameState.players[1]) })}</div>
                    <div className="total-score-value">{gameState.players[1].score}</div>
                  </div>
                </div>
              </div>

              <DiceContainer
                dice={gameState.dice}
                phase={gameState.phase}
                onDieClick={handleDieClick}
              />

              <LockedDiceContainer dice={gameState.dice} />

              <div className="score-panel">
                <div className="score-row">
                  <div className="score-item">
                    <div className="score-label">{t('turnScore')}</div>
                    <div className="score-value">{currentPlayer.turnScore}</div>
                  </div>
                  <div className="score-item">
                    <div className="score-label">{t('selectionScore')}</div>
                    <div className="score-value">{selectionScore}</div>
                  </div>
                </div>
              </div>

              <GameControls
                phase={gameState.phase}
                onRoll={handleRoll}
                onBank={handleBank}
                onNewGame={handleStartGame}
                selectionScore={selectionScore}
                isSelectionValid={hasValidSelection(gameState.dice)}
                isComputerTurn={isComputerTurn}
                hasSelectedDice={hasSelectedDice}
              />
            </Col>
          </Row>

          <GameModal
            show={showModal}
            onHide={handleCloseModal}
            title={modalContent.title}
            content={modalContent.content}
            onConfirm={isForfeitConfirmation ? handleConfirmForfeit : undefined}
            showConfirmButton={isForfeitConfirmation}
          />

          <RewardDiceModal
            show={showRewardModal}
            onHide={handleCloseRewardModal}
            rewardDice={rewardDice}
          />
        </Container>
      )}

      <DiceSelector
        show={showDiceSelector}
        onHide={() => setShowDiceSelector(false)}
        currentConfig={gameState.diceConfigs}
        onSave={handleSaveDiceConfig}
        ownedDice={gameState.players[0].ownedDice}
      />

      <DiceShop
        show={showDiceShop}
        onHide={() => setShowDiceShop(false)}
        playerGroschen={gameState.players[0].groschen}
        ownedDice={gameState.players[0].ownedDice}
        onBuyDice={handleBuyDice}
        onSellDice={handleSellDice}
        currentConfig={gameState.diceConfigs}
      />
    </>
  );
};

export default Game;
